import {
  DEFAULT_INVITE_BUNDLE,
  GRANT_AMOUNT_MAX,
  GRANT_REASON_MAX_CHARS,
} from '@developerz/domain';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ProvisionInput } from '../lib/admin-provision';
import { AdminGrantDuration } from './AdminGrantDuration';
import { Field } from './Field';

// The "Invite by email" form (plan 102 slice 05) — presenter for
// `<AdminInviteByEmailDialog/>`; the body it builds is `lib/admin-provision.ts`'s.
//
// THE BUNDLE IS THE DEFAULT, AND IT IS SENT AS NOTHING. With "Use the invite bundle"
// ticked and the duration untouched, the body names no capacity at all, which is the
// route's own bundle (`DEFAULT_INVITE_BUNDLE`): one definition, the server's. A
// changed duration rides as `expires_in_days` (replacing the bundle's days, `null` =
// ongoing); unticking the bundle sends `ai_devs` and the duration explicitly.
//
// FUNDING AN ORG IS EXPLICIT (decision 6). `account_id` is sent only while "Fund an
// org they own" is ticked; the server checks the person owns it (`not_owner`) and
// refuses it for someone not yet signed in (`not_bookable`), in its own words.
//
// No client validation beyond the inputs' own: the route's refusal is rendered
// verbatim under the fields, like `<AdminInviteForm/>`.

const BUNDLE_DAYS = String(DEFAULT_INVITE_BUNDLE.expiresInDays);

export function AdminInviteByEmailForm(props: {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSend: (input: ProvisionInput) => void;
}): JSX.Element {
  const [email, setEmail] = createSignal('');
  const [reason, setReason] = createSignal('');
  const [bundle, setBundle] = createSignal(true);
  const [aiDevs, setAiDevs] = createSignal(String(DEFAULT_INVITE_BUNDLE.aiDevs));
  const [days, setDays] = createSignal(BUNDLE_DAYS);
  const [ongoing, setOngoing] = createSignal(false);
  const [fundOrg, setFundOrg] = createSignal(false);
  const [accountId, setAccountId] = createSignal('');

  /** The duration, or nothing when it is the bundle's own (bundle mode only). */
  const duration = (): Pick<ProvisionInput, 'expires_in_days'> => {
    if (ongoing()) return { expires_in_days: null };
    if (bundle() && days().trim() === BUNDLE_DAYS) return {};
    return { expires_in_days: Number(days()) };
  };

  const send = (): void => {
    if (props.busy) return;
    const account = accountId().trim();
    props.onSend({
      email: email().trim(),
      reason: reason().trim(),
      ...(bundle() ? {} : { ai_devs: Number(aiDevs()) }),
      ...duration(),
      ...(fundOrg() && account !== '' ? { account_id: account } : {}),
    });
  };

  return (
    <form
      id="admin-invite-email-form"
      class="field-group"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <Field label={t('admin.invite_email_field_email')} id="invite-email-address">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="email"
            required
            maxlength="320"
            disabled={props.busy}
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
          />
        )}
      </Field>
      <label class="admin-detail__check">
        <input
          type="checkbox"
          checked={bundle()}
          disabled={props.busy}
          onChange={(e) => setBundle(e.currentTarget.checked)}
        />
        {t('admin.invite_email_use_bundle', {
          ai_devs: DEFAULT_INVITE_BUNDLE.aiDevs,
          days: DEFAULT_INVITE_BUNDLE.expiresInDays,
        })}
      </label>
      <Show when={!bundle()}>
        <Field
          label={t('admin.access_field_ai_devs')}
          id="invite-email-ai-devs"
          desc={t('admin.access_field_ai_devs_desc', { max: GRANT_AMOUNT_MAX })}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="number"
              min="1"
              max={GRANT_AMOUNT_MAX}
              step="1"
              disabled={props.busy}
              value={aiDevs()}
              onInput={(e) => setAiDevs(e.currentTarget.value)}
            />
          )}
        </Field>
      </Show>
      <AdminGrantDuration
        id="invite-email"
        busy={props.busy}
        days={days()}
        ongoing={ongoing()}
        onDays={setDays}
        onOngoing={setOngoing}
      />
      <p class="field__desc">{t('admin.access_field_boxes_derived')}</p>
      <label class="admin-detail__check">
        <input
          type="checkbox"
          checked={fundOrg()}
          disabled={props.busy}
          onChange={(e) => setFundOrg(e.currentTarget.checked)}
        />
        {t('admin.invite_email_fund_org')}
      </label>
      <Show when={fundOrg()}>
        <Field
          label={t('admin.invite_email_field_account')}
          id="invite-email-account"
          desc={t('admin.invite_email_field_account_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              placeholder="acc_…"
              disabled={props.busy}
              value={accountId()}
              onInput={(e) => setAccountId(e.currentTarget.value)}
            />
          )}
        </Field>
      </Show>
      <Field
        label={t('admin.invite_email_field_reason')}
        id="invite-email-reason"
        desc={t('admin.access_field_reason_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows="2"
            maxlength={GRANT_REASON_MAX_CHARS}
            required
            disabled={props.busy}
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
          />
        )}
      </Field>
      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
    </form>
  );
}
